import { countHotelNights } from "@/lib/daycare-hotel/stayBilling";

export type ReservationPlanOrigin = "existing";
export type StayLineStatus = "charged" | "paid_by_plan" | "not_configured";

export interface ReservationBillingInput {
  moduleType: "day_care" | "hotel";
  requestedDays: number;
  dailyRate: number | null;
  plan: { origin: ReservationPlanOrigin; availableDays: number; price: number } | null;
}

export interface ReservationBillingResult {
  coveredDays: number;
  uncoveredDays: number;
  planCharge: number;
  stayCharge: number;
  stayLineStatus: StayLineStatus;
}

export interface AutomaticStayLine {
  itemId: string | null;
  itemName: string;
  quantity: number;
  unitPrice: number;
  billingRole: "stay";
  status: StayLineStatus | "plan_charge";
  stayDates?: string[];
  waived?: boolean;
}

export function calculateReservationBilling(input: ReservationBillingInput): ReservationBillingResult {
  const requestedDays = Math.max(0, Math.floor(input.requestedDays));
  const availableDays = Math.max(0, Math.floor(input.plan?.availableDays ?? 0));
  const coveredDays = Math.min(requestedDays, availableDays);
  const uncoveredDays = Math.max(0, requestedDays - coveredDays);
  const dailyRate = input.dailyRate !== null && Number.isFinite(input.dailyRate) ? Math.max(0, input.dailyRate) : null;
  const planCharge = 0;
  const stayCharge = dailyRate === null ? 0 : uncoveredDays * dailyRate;

  return {
    coveredDays,
    uncoveredDays,
    planCharge,
    stayCharge,
    stayLineStatus: dailyRate === null && uncoveredDays > 0
      ? "not_configured"
      : uncoveredDays === 0 && coveredDays > 0
        ? "paid_by_plan"
        : "charged",
  };
}

export function getHotelReservationNights(checkInDate: string, checkOutDate: string | null | undefined): number {
  return checkOutDate ? countHotelNights(checkInDate, checkOutDate) : 0;
}

export function buildAutomaticStayLines(input: {
  moduleType: "day_care" | "hotel";
  requestedDays: number;
  dailyRate: number | null;
  dailyServiceName: string;
  adHocDailyName?: string;
  billing: ReservationBillingResult;
  includeUncoveredCharge?: boolean;
  stayDates?: string[];
  waived?: { daily?: boolean };
}): AutomaticStayLine[] {
  const lines: AutomaticStayLine[] = [];
  const adHocDailyName = input.adHocDailyName ?? input.dailyServiceName;
  const chargeDates = input.stayDates?.slice(0, Math.max(0, Math.floor(input.requestedDays)));

  if (input.billing.coveredDays > 0) {
    lines.push({
      itemId: null,
      itemName: input.dailyServiceName,
      quantity: input.billing.coveredDays,
      unitPrice: 0,
      billingRole: "stay",
      status: "paid_by_plan",
      stayDates: chargeDates?.slice(0, input.billing.coveredDays),
    });
  }

  if (input.billing.uncoveredDays > 0 && input.includeUncoveredCharge !== false) {
    const chargeable = input.dailyRate !== null;
    const waived = chargeable && Boolean(input.waived?.daily);
    lines.push({
      itemId: null,
      itemName: adHocDailyName,
      quantity: input.billing.uncoveredDays,
      unitPrice: waived ? 0 : (input.dailyRate ?? 0),
      billingRole: "stay",
      status: chargeable ? "charged" : "not_configured",
      stayDates: chargeDates?.slice(input.billing.coveredDays),
      waived: waived || undefined,
    });
  }

  return lines;
}
